'use client'

import Link from 'next/link'
import { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import useIsFreeUser from '@crystal-eyes/hooks/user/useIsFreeUser'
import SectionCard from '../components/SectionCard'
import { Personality } from '@crystal-eyes/types'
import styles from './DownloadGraphics.module.scss'
import useSocialImage, {
  ImageTypes,
} from '@crystal-eyes/hooks/profiles/useSocialImage'
import emailTopper from '@dashboard/public/static/share/images/signature.png'

const STATIC_IMAGE_PATH = '/static/share/images/profile-preview.png'

interface Identity {
  id: string
}

export default function DownloadGraphics({
  identity,
  personality,
}: {
  identity?: Identity
  personality?: Personality
}) {
  const { data: isFreeUser } = useIsFreeUser()
  const { data: poster } = useSocialImage({
    identity,
    type: ImageTypes.PersonalityPoster,
    width: 180,
    height: 180,
  })
  const personalityPosterLink = isFreeUser
    ? '/app/upgrade'
    : poster?.url || STATIC_IMAGE_PATH

  const { data: social } = useSocialImage({
    identity,
    type: ImageTypes.SocialMedia,
    width: 180,
    height: 180,
  })

  const { data: emailSig } = useSocialImage({
    identity,
    type: ImageTypes.EmailSignature,
    width: 112,
    height: 78,
  })

  const { data: linkedinBanner } = useSocialImage({
    identity,
    type: ImageTypes.LinkedInBanner,
    width: 720,
    height: 180,
  })

  if (!personality?.discType) return null

  return (
    <SectionCard
      cardId="graphics"
      icon={Icons.Download}
      title="Downloads"
      sidebarTitle="Graphics"
      className={styles.downloadGraphics}
    >
      <div className="personality-poster">
        <Link href={personalityPosterLink}>
          <img
            src={poster?.url || STATIC_IMAGE_PATH}
            width={180}
            height={180}
            alt="Usage Example"
            className="usage-example poster-img"
          />

          <span className="label">Personality Poster</span>
        </Link>
      </div>

      <div className="social-media-graphic">
        <Link href={social?.downloadUrl || STATIC_IMAGE_PATH}>
          <img
            src={social?.url || STATIC_IMAGE_PATH}
            width={180}
            height={180}
            alt="Usage Example"
            className="usage-example"
          />

          <span className="label">Social Media Graphic</span>
        </Link>
      </div>

      <div className="email-signature">
        <Link href={emailSig?.downloadUrl || STATIC_IMAGE_PATH}>
          <div className="usage-example">
            <img
              src={emailTopper.src}
              className="signature-top"
              alt="Usage Example (Email Contents Placeholder)"
              width={112}
              height={78}
            />

            <img
              src={emailSig?.url || STATIC_IMAGE_PATH}
              className="signature-bottom"
              alt="Usage Example"
            />
          </div>

          <span className="label">Email Signature</span>
        </Link>
      </div>

      <div className="linkedin-banner">
        <Link href={linkedinBanner?.downloadUrl || STATIC_IMAGE_PATH}>
          <img
            src={linkedinBanner?.url || STATIC_IMAGE_PATH}
            width={720}
            height={180}
            alt="Usage Example"
            className="usage-example"
          />
          <span className="li-label">LinkedIn Banner</span>
        </Link>
      </div>
    </SectionCard>
  )
}
